import type { ApiTestEndpoint } from './apiTestRequest';
import type { OrgFeatureFlagKey } from './orgFeatureFlagKeys';

/**
 * The endpoints the API Testing page lets a user try, and download one at a
 * time as a Postman request. Hand-written: the OpenAPI spec
 * (`src/generated/openapi.json`) is the source of the whole-collection
 * Postman export. `apiTestEndpoints.test.ts` pins the flag-gated groups here
 * to the spec so the two cannot drift.
 */

export const REBATE_PROGRAMS_CATEGORY = 'Rebate Programs';

/**
 * Categories shown only while an organization flag is on. The same flag
 * decides whether the whole-collection Postman export carries the matching
 * spec paths, so the tester and the download agree.
 */
export const FLAG_GATED_CATEGORIES: Readonly<Record<string, OrgFeatureFlagKey>> = {
  [REBATE_PROGRAMS_CATEGORY]: 'rebate_rules_api',
};

export const API_ENDPOINTS: ApiTestEndpoint[] = [
  // Health & Stats
  {
    id: 'health',
    method: 'GET',
    path: '/health',
    description: 'Health check endpoint',
    category: 'System',
    requiresAuth: false,
    requiresApiKey: false,
  },
  {
    id: 'product-showcase',
    plan: 'public',
    method: 'GET',
    path: '/products/showcase',
    description: 'Public marketing demo — latest Canon, Nikon, and Sony cameras (full specs, max 3)',
    category: 'Product Data',
    requiresAuth: false,
    requiresApiKey: false,
  },
  {
    id: 'product-showcase-by-id',
    plan: 'public',
    method: 'GET',
    path: '/products/showcase/:id',
    description: 'Public detail for a current showcase product only',
    category: 'Product Data',
    requiresAuth: false,
    requiresApiKey: false,
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Showcase product ID', example: '' },
    ],
  },
  {
    id: 'product-stats',
    plan: 'all',
    method: 'GET',
    path: '/products/stats',
    description: 'Get product statistics (cameras and accessories)',
    category: 'Product Data',
    requiresAuth: true,
    requiresApiKey: true,
  },

  // Products (Cameras and Accessories)
  {
    id: 'get-products',
    plan: 'pro',
    method: 'GET',
    path: '/products',
    description: 'Get all products (paginated) - includes cameras and accessories',
    category: 'Product Data',
    requiresAuth: true,
    requiresApiKey: true,
    parameters: [
      { name: 'limit', type: 'number', required: false, description: 'Number of products per page', example: '10' },
      { name: 'offset', type: 'number', required: false, description: 'Number of products to skip', example: '0' },
      { name: 'productType', type: 'string', required: false, description: 'Filter by product type (camera or accessory)', example: 'camera' },
      { name: 'search', type: 'string', required: false, description: 'Search text (name, model, SKU, brand); answers like /products/search', example: '120-300' },
      { name: 'includeUnreleased', type: 'string', required: false, description: 'Announced and pre-order products are included by default; send false to leave them out', example: 'false' },
    ],
  },
  {
    id: 'get-product-by-id',
    plan: 'pro',
    method: 'GET',
    path: '/products/:id',
    description: 'Get specific product by ID',
    category: 'Product Data',
    requiresAuth: true,
    requiresApiKey: true,
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Product ID', example: '65fa0295190b7fc0c2cf04ad' },
    ],
  },
  {
    id: 'search-products',
    plan: 'pro',
    method: 'GET',
    path: '/products/search',
    description: 'Search products (cameras and accessories)',
    category: 'Product Data',
    requiresAuth: true,
    requiresApiKey: true,
    parameters: [
      { name: 'q', type: 'string', required: true, description: 'Search query', example: 'DSLR' },
      { name: 'limit', type: 'number', required: false, description: 'Number of results', example: '10' },
      { name: 'offset', type: 'number', required: false, description: 'Number of results to skip', example: '0' },
      { name: 'includeUnreleased', type: 'string', required: false, description: 'Announced and pre-order products are included by default; send false to leave them out', example: 'false' },
    ],
  },
  {
    id: 'get-products-by-category',
    plan: 'pro',
    method: 'GET',
    path: '/products/category/:category',
    description: 'Get products by category',
    category: 'Product Data',
    requiresAuth: true,
    requiresApiKey: true,
    parameters: [
      { name: 'category', type: 'string', required: true, description: 'Product category', example: 'dslr-cameras' },
      { name: 'limit', type: 'number', required: false, description: 'Number of products per page', example: '10' },
      { name: 'offset', type: 'number', required: false, description: 'Number of products to skip', example: '0' },
    ],
  },
  {
    id: 'get-products-by-brand',
    plan: 'pro',
    method: 'GET',
    path: '/products/brand/:brand',
    description: 'Get products by brand',
    category: 'Product Data',
    requiresAuth: true,
    requiresApiKey: true,
    parameters: [
      { name: 'brand', type: 'string', required: true, description: 'Product brand', example: 'Nikon' },
      { name: 'limit', type: 'number', required: false, description: 'Number of products per page', example: '10' },
      { name: 'offset', type: 'number', required: false, description: 'Number of products to skip', example: '0' },
    ],
  },

  // API Key Management
  {
    id: 'get-api-keys',
    plan: 'session',
    method: 'GET',
    path: '/api-keys',
    description: 'Get all API keys for the current user',
    category: 'API Keys',
    requiresAuth: true,
    requiresApiKey: false,
  },
  {
    id: 'create-api-key',
    plan: 'session',
    method: 'POST',
    path: '/api-keys',
    description: 'Create a new API key',
    category: 'API Keys',
    requiresAuth: true,
    requiresApiKey: false,
    body: [
      { name: 'name', type: 'string', required: true, description: 'API key name', example: 'My Integration Key' },
      {
        name: 'permissions',
        type: 'array',
        required: false,
        description: 'Endpoint path grants (e.g. /products, /store/product-overrides) or ["*"] for full access',
        example: ['/products', '/store/product-overrides'],
      },
    ],
  },
  {
    id: 'delete-api-key',
    plan: 'session',
    method: 'DELETE',
    path: '/api-keys/:id',
    description: 'Delete an API key',
    category: 'API Keys',
    requiresAuth: true,
    requiresApiKey: false,
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'API key ID', example: 'abc123' },
    ],
  },

  // Integrations
  {
    id: 'get-integrations',
    plan: 'session',
    method: 'GET',
    path: '/integrations',
    description: 'Get all integrations for the current user',
    category: 'Integrations',
    requiresAuth: true,
    requiresApiKey: true,
  },
  {
    id: 'create-integration',
    plan: 'session',
    method: 'POST',
    path: '/integrations',
    description: 'Create a new integration',
    category: 'Integrations',
    requiresAuth: true,
    requiresApiKey: true,
    body: [
      { name: 'platform', type: 'string', required: true, description: 'Integration platform', example: 'bigcommerce' },
      { name: 'name', type: 'string', required: true, description: 'Integration name', example: 'My BigCommerce Store' },
      { name: 'isActive', type: 'boolean', required: false, description: 'Whether integration is active', example: 'true' },
      { name: 'credentials', type: 'object', required: true, description: 'Platform credentials', example: '{"apiKey": "abc123"}' },
      { name: 'settings', type: 'object', required: false, description: 'Integration settings', example: '{"syncProducts": true}' },
    ],
  },

  // Store product overrides (title / description / tags / pricing) — org-scoped
  {
    id: 'get-store-product-overrides',
    plan: 'all',
    method: 'GET',
    path: '/store/product-overrides',
    description:
      'List store product overrides for your organization, or fetch one by productId. Store-scoped only — does not change the canonical catalog.',
    category: 'Store Overrides',
    requiresAuth: true,
    requiresApiKey: true,
    orgScoped: true,
    parameters: [
      {
        name: 'productId',
        type: 'string',
        required: false,
        description: 'When set, returns a single override (or null)',
        example: '65fa0295190b7fc0c2cf04ad',
      },
    ],
  },
  {
    id: 'upsert-store-product-override',
    plan: 'all',
    method: 'PUT',
    path: '/store/product-overrides/:productId',
    description:
      'Create or partially update a store product override (title, description, tags, pricing). Only fields present in the body are written; null clears metadata fields. Applied on the next ERP sync (Shopify title/body/tags).',
    category: 'Store Overrides',
    requiresAuth: true,
    requiresApiKey: true,
    orgScoped: true,
    parameters: [
      {
        name: 'productId',
        type: 'string',
        required: true,
        description: 'Canonical Motzu product ID',
        example: '65fa0295190b7fc0c2cf04ad',
      },
    ],
    body: [
      {
        name: 'overrideTitle',
        type: 'string',
        required: false,
        description: 'Store-specific product title (null to clear)',
        example: 'Sony A7 IV Body - Impulse Exclusive',
      },
      {
        name: 'overrideDescription',
        type: 'string',
        required: false,
        description: 'Store-specific description HTML/text (null to clear)',
        example: 'Custom store description for this SKU.',
      },
      {
        name: 'overrideTags',
        type: 'array',
        required: false,
        description:
          'When set (including []), replaces Motzu auto Shopify tags for this product. null restores Motzu auto tags. motzu_synced may still be added.',
        example: ['Impulse', 'Mirrorless', 'Full Frame'],
      },
      {
        name: 'overridePrice',
        type: 'number',
        required: false,
        description: 'Optional store price override',
        example: 2499.99,
      },
      {
        name: 'brand',
        type: 'string',
        required: false,
        description: 'Optional brand label stored with the override',
        example: 'Sony',
      },
      {
        name: 'model',
        type: 'string',
        required: false,
        description: 'Optional model label stored with the override',
        example: 'A7 IV',
      },
      {
        name: 'sku',
        type: 'string',
        required: false,
        description: 'Optional SKU label stored with the override',
        example: 'ILCE7M4',
      },
    ],
  },
  {
    id: 'delete-store-product-override',
    plan: 'all',
    method: 'DELETE',
    path: '/store/product-overrides/:productId',
    description: 'Delete all store overrides for a product (catalog defaults resume on next sync)',
    category: 'Store Overrides',
    requiresAuth: true,
    requiresApiKey: true,
    orgScoped: true,
    parameters: [
      {
        name: 'productId',
        type: 'string',
        required: true,
        description: 'Canonical Motzu product ID',
        example: '65fa0295190b7fc0c2cf04ad',
      },
    ],
  },

  // Rebate rules API (docs/internal/API_REBATE_RULES.md). Shown only while the
  // organization has `rebate_rules_api` on — see FLAG_GATED_CATEGORIES.
  {
    id: 'list-rebate-programs',
    plan: 'pro',
    method: 'GET',
    path: '/rebate-programs',
    description:
      "Active and upcoming rebate programs for the key's catalog scope, plus one bundle program per group of live bundle rules. Oldest change first; each program carries up to 200 products, with productCount and productsUrl for the rest. An API key with a custom data scope needs the rebate_rules scope.",
    category: REBATE_PROGRAMS_CATEGORY,
    requiresAuth: true,
    requiresApiKey: true,
    orgScoped: true,
    parameters: [
      {
        name: 'updatedSince',
        type: 'string',
        required: false,
        description: 'Only programs changed after this ISO 8601 timestamp',
        example: '',
      },
      { name: 'brand', type: 'string', required: false, description: 'Restrict to one brand', example: '' },
      { name: 'page', type: 'number', required: false, description: 'Page number, from 1', example: '1' },
      {
        name: 'pageSize',
        type: 'number',
        required: false,
        description: 'Programs per page (default 25, at most 100)',
        example: '25',
      },
    ],
  },
  {
    id: 'list-rebate-program-products',
    plan: 'pro',
    method: 'GET',
    path: '/rebate-programs/:id/products',
    description:
      "One rebate program's full product list (offers first, then qualifying products of a bundle), paged. An API key with a custom data scope needs the rebate_rules scope.",
    category: REBATE_PROGRAMS_CATEGORY,
    requiresAuth: true,
    requiresApiKey: true,
    orgScoped: true,
    parameters: [
      {
        name: 'id',
        type: 'string',
        required: true,
        description: 'Rebate program id from GET /rebate-programs',
        example: '',
      },
      {
        name: 'role',
        type: 'string',
        required: false,
        description: 'offer or qualifier — only offer products or only qualifying products',
        example: '',
      },
      { name: 'page', type: 'number', required: false, description: 'Page number, from 1', example: '1' },
      {
        name: 'pageSize',
        type: 'number',
        required: false,
        description: 'Products per page (default 100, at most 500)',
        example: '100',
      },
    ],
  },
  {
    id: 'get-product-rebates',
    plan: 'pro',
    method: 'GET',
    path: '/products/:id/rebates',
    description:
      'Every live rebate program and bundle a product takes part in, with its role in each (offer earns the rebate, qualifier is what an offer must be sold with). An API key with a custom data scope needs the rebate_rules scope.',
    category: REBATE_PROGRAMS_CATEGORY,
    requiresAuth: true,
    requiresApiKey: true,
    orgScoped: true,
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Product ID', example: '65fa0295190b7fc0c2cf04ad' },
    ],
  },
];

/** `/products/:id/rebates` -> `/products/{id}/rebates`, the spec's path form. */
export function toOpenApiPath(path: string): string {
  return path.replace(/:([a-zA-Z0-9_]+)/g, '{$1}');
}

/** Drops every endpoint whose category is gated by a flag that is off. */
export function filterFlagGatedEndpoints(
  endpoints: ApiTestEndpoint[],
  isFlagEnabled: (key: OrgFeatureFlagKey) => boolean,
): ApiTestEndpoint[] {
  return endpoints.filter((endpoint) => {
    const flag = FLAG_GATED_CATEGORIES[endpoint.category];
    return !flag || isFlagEnabled(flag);
  });
}

/**
 * Spec paths the whole-collection Postman export must leave out: those of
 * every tester endpoint in a flag-gated category whose flag is off.
 */
export function flagGatedSpecPathsToOmit(
  endpoints: ApiTestEndpoint[],
  isFlagEnabled: (key: OrgFeatureFlagKey) => boolean,
): string[] {
  const visible = new Set(filterFlagGatedEndpoints(endpoints, isFlagEnabled));
  return Array.from(
    new Set(endpoints.filter((endpoint) => !visible.has(endpoint)).map((endpoint) => toOpenApiPath(endpoint.path))),
  );
}
